import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Box } from "../../../components/ui";
import { campusApi } from "../campus-api";
import { Node } from "../types";
import { modalStyle } from "../../../style/common";

interface TokenModalProps {
  open: boolean;
  onClose: () => void;
  node: Node | null;
}

export default function TokenModal({ open, onClose, node }: TokenModalProps) {
  const [expiryDays, setExpiryDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<string | null>(null);
  const [isNewToken, setIsNewToken] = useState(false);

  useEffect(() => {
    if (open && node) {
      setExpiryDays(30);
      setIsNewToken(false);
      // Show existing token if node has one
      if (node.access_token) {
        setGeneratedToken(node.access_token);
        setTokenExpiry(node.tokenExpiry || null);
      } else {
        setGeneratedToken(null);
        setTokenExpiry(null);
      }
    }
  }, [open, node]);

  const handleGenerate = async () => {
    if (!node) return;

    setLoading(true);
    try {
      // Use regenerate if token exists, otherwise generate
      const updatedNode = node.access_token
        ? await campusApi.regenerateNodeToken({
            nodeId: node.node_id,
            expiryDays,
          })
        : await campusApi.generateNodeToken({
            nodeId: node.node_id,
            expiryDays,
          });
      setGeneratedToken(updatedNode.access_token || "");
      setTokenExpiry(updatedNode.tokenExpiry || "");
      setIsNewToken(true);
      toast.success(node.access_token ? "Token regenerated successfully" : "Token generated successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || (node.access_token ? "Failed to regenerate token" : "Failed to generate token"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = async () => {
    if (generatedToken) {
      try {
        await navigator.clipboard.writeText(generatedToken);
        toast.success("Token copied to clipboard");
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = generatedToken;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
          toast.success("Token copied to clipboard");
        } catch (err) {
          toast.error("Failed to copy token");
        }
        document.body.removeChild(textArea);
      }
    }
  };

  if (!node) return null;

  return (
    <Modal onClose={onClose} open={open}>
      <Box sx={modalStyle}>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {node.access_token ? "Regenerate Access Token" : "Generate Access Token"}
          </h2>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Node ID</p>
              <p className="font-mono text-sm text-gray-900 dark:text-white">{node.id}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Token Expiry (Days)
              </label>
              <input
                type="number"
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
                min="1"
                max="365"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter expiry days"
              />
            </div>

            {generatedToken && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    {isNewToken ? "Generated Token" : "Current Token"}
                  </p>
                  <button
                    onClick={handleCopyToken}
                    className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors"
                    title="Copy token"
                  >
                    <i className="fa-solid fa-copy"></i>
                  </button>
                </div>
                <p className="font-mono text-xs text-green-900 dark:text-green-200 break-all mb-2">
                  {generatedToken}
                </p>
                {tokenExpiry && (
                  <p className="text-xs text-green-700 dark:text-green-400">
                    Expires: {new Date(tokenExpiry).toLocaleString()}
                  </p>
                )}
                {!node.access_token && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                    ⚠️ Save this token securely. You won't be able to see it again.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {generatedToken ? "Close" : "Cancel"}
              </button>
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading 
                  ? (node.access_token ? "Regenerating..." : "Generating...")
                  : (node.access_token ? "Regenerate Token" : "Generate Token")
                }
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

